import React, { useEffect, useMemo, useRef, useState } from "react";
import Chart from "chart.js/auto";
import { plotDefinitions } from "../data/weather/plotDefinitions";
import { formatDisplayDate } from "../utils/date";

const WINDOW_DAYS = 30;

function parseHistoricalDate(dateString) {
  return new Date(
    Number(dateString.slice(0, 4)),
    Number(dateString.slice(4, 6)) - 1,
    Number(dateString.slice(6, 8))
  );
}

function toDateInputValue(dateString) {
  return `${dateString.slice(0, 4)}-${dateString.slice(4, 6)}-${dateString.slice(6, 8)}`;
}

function buildLabels(historicalDates) {
  return historicalDates.map((dateString) => {
    return formatDisplayDate(parseHistoricalDate(dateString)).slice(0, -5);
  });
}

function getDefaultRangeStart(historicalDates) {
  if (historicalDates.length === 0) {
    return "";
  }

  const startIndex = Math.max(0, historicalDates.length - WINDOW_DAYS);
  return toDateInputValue(historicalDates[startIndex]);
}

function getRangeBounds(historicalDates, rangeStart) {
  if (historicalDates.length === 0) {
    return { startIndex: 0, endIndex: 0 };
  }

  const selectedIndex = historicalDates.findIndex(
    (dateString) => toDateInputValue(dateString) === rangeStart
  );
  const startIndex = selectedIndex === -1 ? Math.max(0, historicalDates.length - WINDOW_DAYS) : selectedIndex;

  return {
    startIndex,
    endIndex: Math.min(startIndex + WINDOW_DAYS, historicalDates.length),
  };
}

const DataPlotsPage = ({ historicalSeries, historicalDates, statusMessage }) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const [selectedPlot, setSelectedPlot] = useState(plotDefinitions[0].value);
  const [rangeStart, setRangeStart] = useState("");
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (historicalDates.length === 0) {
      return;
    }

    const validStarts = historicalDates
      .slice(0, Math.max(1, historicalDates.length - WINDOW_DAYS + 1))
      .map(toDateInputValue);

    if (!rangeStart || !validStarts.includes(rangeStart)) {
      setRangeStart(getDefaultRangeStart(historicalDates));
    }
  }, [historicalDates, rangeStart]);

  const rangeBounds = useMemo(
    () => getRangeBounds(historicalDates, rangeStart),
    [historicalDates, rangeStart]
  );
  const filteredDates = useMemo(
    () => historicalDates.slice(rangeBounds.startIndex, rangeBounds.endIndex),
    [historicalDates, rangeBounds]
  );
  const selectedSeries = useMemo(() => {
    const series = historicalSeries?.[selectedPlot] ?? [];
    return series.map((item) => ({
      ...item,
      data: item.data.slice(rangeBounds.startIndex, rangeBounds.endIndex),
    }));
  }, [historicalSeries, rangeBounds, selectedPlot]);
  const activePlot = plotDefinitions.find((plot) => plot.value === selectedPlot);
  const hasData = selectedSeries.some((series) => series.data.some((value) => value !== null));
  const firstDate = historicalDates[0];
  const maxStartIndex = Math.max(0, historicalDates.length - WINDOW_DAYS);
  const rangeMin = firstDate ? toDateInputValue(firstDate) : "";
  const rangeMax = historicalDates[maxStartIndex] ? toDateInputValue(historicalDates[maxStartIndex]) : "";
  const rangeLabel =
    filteredDates.length > 0
      ? `${formatDisplayDate(parseHistoricalDate(filteredDates[0]))} to ${formatDisplayDate(
          parseHistoricalDate(filteredDates[filteredDates.length - 1])
        )}`
      : "";

  useEffect(() => {
    if (!chartRef.current || !activePlot || !hasData) {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
      return undefined;
    }

    const labels = buildLabels(filteredDates);
    const context = chartRef.current.getContext("2d");

    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
    }

    chartInstanceRef.current = new Chart(context, {
      type: "line",
      data: {
        labels,
        datasets: selectedSeries.map((series) => ({
          label: series.label,
          data: series.data,
          borderColor: series.borderColor,
          backgroundColor: series.backgroundColor,
          fill: false,
          tension: 0.1,
          borderWidth: 1,
        })),
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: "index",
        },
        plugins: {
          legend: {
            position: "bottom",
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: `(${activePlot.unit})`,
            },
          },
          x: {
            title: {
              display: true,
              text: "Date",
            },
          },
        },
      },
    });

    const handleResize = () => {
      chartInstanceRef.current?.resize();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      chartInstanceRef.current?.destroy();
      chartInstanceRef.current = null;
      window.removeEventListener("resize", handleResize);
    };
  }, [activePlot, filteredDates, hasData, selectedSeries]);

  return (
    <div className="page-content">
      <h1>Weather History</h1>

      {statusMessage ? <div className="status-banner">{statusMessage}</div> : null}

      <div
        className="info-icon"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        ⓘ
        {isHovered ? (
          <div className="tooltip">
            Empty data points indicate the weather station was offline or not returning that
            specific metric for that day.
          </div>
        ) : null}
      </div>

      <div className="plot-controls">
        <label className="plot-control">
          <span>Metric</span>
          <select id="plotSelect" value={selectedPlot} onChange={(event) => setSelectedPlot(event.target.value)}>
            {plotDefinitions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <label className="plot-control">
          <span>30-day range</span>
          <input
            type="date"
            value={rangeStart}
            min={rangeMin}
            max={rangeMax}
            onChange={(event) => setRangeStart(event.target.value)}
            disabled={!rangeStart}
          />
        </label>

        {rangeLabel ? <div className="plot-range-label">{rangeLabel}</div> : null}
      </div>

      {hasData ? (
        <div className="plot-wrapper">
          <canvas ref={chartRef} className="plot-canvas" />
        </div>
      ) : (
        <div className="plot-empty-state">
          Saved weather station history is unavailable right now.
        </div>
      )}
    </div>
  );
};

export default DataPlotsPage;
